import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';

const AddTemplateComponent = ({ show, onHide, templateId, onComponentAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [payComponents, setPayComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [existingComponentIds, setExistingComponentIds] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    component_id: '',
    component_type: 'Earnings',
    calculation_type: 'Fixed',
    value: '',
    component_order: 1,
    is_active: 1
  });

  const API_BASE = process.env.REACT_APP_API_BASE;

  // Filter components based on selected type
  useEffect(() => {
    if (payComponents.length > 0) {
      const filtered = payComponents.filter(component => {
        // Check if component type matches
        const typeMatches = component.component_type === formData.component_type;
        // Check if component is not already in template
        const notInTemplate = !existingComponentIds.includes(component.component_id);
        // Check if component is active
        const isActive = component.is_active === 1 || component.is_active === true;
        return typeMatches && notInTemplate && isActive;
      });
      setFilteredComponents(filtered);

      // Auto-select first component if available and none selected
      if (filtered.length > 0 && !formData.component_id && filtered[0].component_id) {
        setFormData(prev => ({ ...prev, component_id: filtered[0].component_id }));
      }
    }
  }, [payComponents, formData.component_type, formData.component_id, existingComponentIds]);

  const fetchPayComponents = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_pay_components.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // We need to fetch full component data including type
        const fullComponents = await fetchAllComponentDetails(response.data.components);
        setPayComponents(fullComponents);
      } else {
        setError(response.data.error || 'Failed to fetch pay components');
      }
    } catch (error) {
      setError('Failed to fetch pay components: ' + error.message);
      console.error('Error fetching pay components:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Since get_pay_components.php only returns name and is_active, we need another endpoint
  // or modify existing one. For now, I'll assume we need to fetch all details.
  // Let's check if we need to create a new endpoint or use existing one.
  const fetchAllComponentDetails = async (basicComponents) => {
    try {
      // Mock structure based on basic data
      // In production, you should create a proper endpoint
      return basicComponents.map(comp => ({
        component_id: comp.component_id || 0,
        component_name: comp.component_name,
        component_type: comp.component_type || 'Earning', // Default type
        calculation_type: comp.calculation_type || 'Fixed', // Default calculation
        is_active: comp.is_active
      }));
    } catch (error) {
      console.error('Error fetching component details:', error);
      return [];
    }
  };

  const fetchExistingComponents = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/template_details.php`, {
        params: { template_id: templateId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const existingIds = response.data.details.map(detail => detail.component_id);
        setExistingComponentIds(existingIds);
      }
    } catch (error) {
      console.error('Error fetching existing components:', error);
    }
  }, [API_BASE, templateId]);

  // Fetch pay components when modal opens
  useEffect(() => {
    if (show) {
      fetchPayComponents();
      fetchExistingComponents();
    }
  }, [show, fetchPayComponents, fetchExistingComponents]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleComponentTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prev => ({ ...prev, component_type: newType }));
  };

  const handleComponentSelect = (e) => {
    const componentId = e.target.value;
    const selectedComponent = payComponents.find(c => String(c.component_id) === String(componentId));

    if (selectedComponent) {
      setFormData(prev => ({
        ...prev,
        component_id: componentId,
        calculation_type: selectedComponent.calculation_type || 'Fixed'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.component_id) {
      setError('Please select a component');
      return;
    }

    if (!formData.value.trim()) {
      setError('Please enter a value');
      return;
    }

    if (formData.component_order < 1) {
      setError('Order must be at least 1');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('authToken');

      const payload = {
        template_id: templateId,
        component_id: formData.component_id,
        component_type: formData.component_type,
        calculation_type: formData.calculation_type,
        value: formData.value,
        component_order: parseInt(formData.component_order),
        is_active: formData.is_active
      };

      console.log('Submitting payload:', payload);

      const response = await axios.post(`${API_BASE}/add_template_component.php`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Response:', response.data);

      if (response.data.success) {
        setSuccess('Component added successfully');

        // Reset form
        setFormData({
          component_id: '',
          component_type: 'Earnings',
          calculation_type: 'Fixed',
          value: '',
          component_order: (formData.component_order + 1),
          is_active: 1
        });

        // Callback to refresh parent
        setTimeout(() => {
          onComponentAdded();
          onHide();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to add component');
      }
    } catch (error) {
      console.error('Error adding component:', error);
      setError(error.response?.data?.error || 'Failed to add component. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      component_id: '',
      component_type: 'Earnings',
      calculation_type: 'Fixed',
      value: '',
      component_order: 1,
      is_active: 1
    });
    setError('');
    setSuccess('');
    onHide();
  };

  // Since get_pay_components.php doesn't return component_id, we need to modify it
  // Let me check what it returns and adjust accordingly

  return (
    <Modal show={show} onHide={resetForm} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <i className="bi bi-plus-circle me-2 text-success"></i>
          Add Component to Template
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              <i className="bi bi-check-circle me-2"></i>
              {success}
            </Alert>
          )}

          <div className="row">
            {/* Component Type */}
            <div className="col-md-6 mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-type me-1 text-primary"></i>
                  Component Type
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Check
                    type="radio"
                    id="earnings-type"
                    name="component_type"
                    label={
                      <span>
                        <i className="bi bi-plus-circle text-success me-1"></i>
                        Earnings
                      </span>
                    }
                    value="Earnings"
                    checked={formData.component_type === 'Earnings'}
                    onChange={handleComponentTypeChange}
                    disabled={loading}
                  />
                  <Form.Check
                    type="radio"
                    id="deduction-type"
                    name="component_type"
                    label={
                      <span>
                        <i className="bi bi-dash-circle text-danger me-1"></i>
                        Deduction
                      </span>
                    }
                    value="Deduction"
                    checked={formData.component_type === 'Deduction'}
                    onChange={handleComponentTypeChange}
                    disabled={loading}
                  />
                </div>
                <Form.Text className="text-muted">
                  Select whether this is an earning or deduction component
                </Form.Text>
              </Form.Group>
            </div>

            {/* Component Selection */}
            <div className="col-md-6 mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-puzzle me-1 text-primary"></i>
                  Component
                  {filteredComponents.length > 0 && (
                    <span className="badge bg-secondary ms-2">{filteredComponents.length} available</span>
                  )}
                </Form.Label>
                {loading ? (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted">Loading components...</span>
                  </div>
                ) : filteredComponents.length === 0 ? (
                  <Alert variant="warning" className="py-2 mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    No {formData.component_type.toLowerCase()} components available to add
                  </Alert>
                ) : (
                  <Form.Select
                    name="component_id"
                    value={formData.component_id}
                    onChange={handleComponentSelect}
                    required
                    disabled={loading || filteredComponents.length === 0}
                  >
                    <option value="">Select a component</option>
                    {filteredComponents.map(component => (
                      <option key={component.component_id} value={component.component_id}>
                        {component.component_name}
                        {component.calculation_type && ` (${component.calculation_type})`}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Choose from available {formData.component_type.toLowerCase()} components
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Calculation Type and Value */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-calculator me-1 text-primary"></i>
                  Calculation Type
                </Form.Label>
                <Form.Select
                  name="calculation_type"
                  value={formData.calculation_type}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                >
                  <option value="Fixed">
                    <i className="bi bi-currency-dollar me-1"></i>
                    Fixed Amount
                  </option>
                  <option value="Percentage">
                    <i className="bi bi-percent me-1"></i>
                    Percentage
                  </option>
                  <option value="Formula">
                    <i className="bi bi-calculator me-1"></i>
                    Formula
                  </option>
                </Form.Select>
                <Form.Text className="text-muted">
                  How this component should be calculated
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col-md-6 mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-input-cursor-text me-1 text-primary"></i>
                  {formData.calculation_type === 'Fixed' ? 'Amount' :
                    formData.calculation_type === 'Percentage' ? 'Percentage' :
                      'Formula'}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  placeholder={
                    formData.calculation_type === 'Fixed' ? 'e.g., 5000' :
                      formData.calculation_type === 'Percentage' ? 'e.g., 10' :
                        'e.g., basic_salary * 0.5'
                  }
                  required
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  {formData.calculation_type === 'Fixed' ? 'Fixed amount for this component' :
                    formData.calculation_type === 'Percentage' ? 'Percentage value (without % sign)' :
                      'Enter calculation formula (e.g., basic_salary * 0.1)'}
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Order and Status */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-sort-numeric-up me-1 text-primary"></i>
                  Display Order
                </Form.Label>
                <Form.Control
                  type="number"
                  name="component_order"
                  value={formData.component_order}
                  onChange={handleInputChange}
                  min="1"
                  required
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  Determines the order in which this component appears
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col-md-6 mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-toggle-on me-1 text-primary"></i>
                  Status
                </Form.Label>
                <div className="d-flex align-items-center mt-2">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    name="is_active"
                    label={formData.is_active ? 'Active' : 'Inactive'}
                    checked={formData.is_active === 1}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <div className="ms-3">
                    {formData.is_active === 1 ? (
                      <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Active
                      </span>
                    ) : (
                      <span className="badge bg-secondary">
                        <i className="bi bi-x-circle me-1"></i>
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                <Form.Text className="text-muted">
                  Active components are included in salary calculations
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Preview */}
          {formData.component_id && (
            <div className="card bg-light border-0 mt-3">
              <div className="card-body">
                <h6 className="card-title">
                  <i className="bi bi-eye me-2 text-info"></i>
                  Component Preview
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">Selected Component:</small>
                    <div className="fw-semibold">
                      {payComponents.find(c => String(c.component_id) === String(formData.component_id))?.component_name || 'N/A'}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted">Type:</small>
                    <div>
                      <span className={`badge ${formData.component_type === 'Earnings' ? 'bg-success' : 'bg-danger'}`}>
                        {formData.component_type}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted">Calculation:</small>
                    <div>
                      <span className={`badge ${formData.calculation_type === 'Fixed' ? 'bg-primary' :
                        formData.calculation_type === 'Percentage' ? 'bg-info' : 'bg-warning'}`}>
                        {formData.calculation_type}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-12">
                    <small className="text-muted">Value:</small>
                    <div className="fw-semibold text-primary">
                      {formData.value || 'Not set'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={resetForm} disabled={loading}>
            <i className="bi bi-x-circle me-2"></i>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={loading || filteredComponents.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-2"></i>
                Add Component
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddTemplateComponent;